<template>
  <div class="menu">
    <div class="row col-12 m-0 content-align">
      <div
        class="item-align menu-option"
        :class="{ active: activeItem === 'home' }"
        @click="redirect('home')"
      >
        <font-awesome-icon
          :icon="faHouse"
          class="icon-style"
        />
        <label class="menu-option-text">
            Home
        </label>
      </div>
      <div
        class="item-align menu-option"
        :class="{ active: activeItem === 'search' }"
        @click.stop="openHeaderSearch"
      >
        <font-awesome-icon
            :icon="faSearch"
            role="button"
            class="icon-style"
            />
        <label class="menu-option-text">
            Search
        </label>
      </div>
        <button 
          class="mobile-balance-btn" 
          :class="{ 'mobile-balance-btn--has-balance': userStore.user.displayBalance > 0 || userStore.user.displayBalance < -10 }" 
          @click="showDepositModal()"
        >
          <span class="mobile-balance-btn__text">
            {{ getMobileBalanceText() }}
          </span>
        </button>
      <div
        class="item-align menu-option"
        :class="{ active: activeItem === 'myBets' }"
        @click="redirect('myBets')"
      >
        <font-awesome-icon
          :icon="faDiceFive"
          class="icon-style"
        />
        <label class="menu-option-text">
            My Bets
        </label>
      </div>
      <div
        ref="accountLogo"
        tabindex="0"
        class="item-align menu-option"
        :class="{ active: activeItem === 'more' }"
        @click.stop="toggleAccountPopUp('icon')"
      >
        <font-awesome-icon
          :icon="faUser"
          class="icon-style"
        />
        <label class="menu-option-text">
            More
        </label>
        <!-- Dropdowns positioned relative to icon-wrapper -->
        <account-dropdown
          :show-popup="showAccountPopUp"
          @close-pop-up="toggleAccountPopUp('component')"
        />
      </div>
    </div>
    <!-- Invisible input focused within the Search tap so iOS opens the keyboard
         immediately and keeps it up across the my-bets -> home route change,
         until the pill's input mounts and focus transfers to it. -->
    <input
      ref="searchKeyboardKeeper"
      class="search-keyboard-keeper"
      type="text"
      tabindex="-1"
      aria-hidden="true"
      autocomplete="off"
      autocorrect="off"
      autocapitalize="off"
      spellcheck="false"
    />
  </div>
</template>

<script setup>
import { ref, computed, provide } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faSearch } from '@fortawesome/free-solid-svg-icons/faSearch';
import { faUser} from '@fortawesome/pro-regular-svg-icons/faUser';
import { faDiceFive} from '@fortawesome/pro-regular-svg-icons/faDiceFive';
import { useUserStore } from '@/stores/user';
import { faHouse } from '@fortawesome/free-solid-svg-icons/faHouse';
import { emitter } from '@/core/emitter.js';
import { userCanUseCashier } from '@/consts/constants';
import { requestMobileSearch, mobileSearchOpen } from '@/core/mobileSearch';
import { PostHogUtils } from '@/core/PostHogUtils';
import AccountDropdown from '@/components/global/header/AccountDropdown.vue';

const router = useRouter();
const route = useRoute();

// Routes whose view renders the search pill (and consumes the request when
// its header mounts — even if the page is still loading). Any other route
// has no pill, so the bottom-bar search falls back to the home surface.
const PILL_SEARCH_ROUTES = new Set([
  'HomePage',
  'AllProps',
  'AllPropsExchange',
  'AllPropsSportsbook',
  'Exchange',
  'GamesPage',
  'SingleGameMarket',
]);

const accountLogo = ref(null);
const balanceElement = ref(null);
const searchKeyboardKeeper = ref(null);
// Create a computed property that flattens the ignore elements
const ignoreElements = computed(() =>
  [accountLogo.value, balanceElement.value].filter(Boolean),
);
provide('ignoreElement', ignoreElements);
const userStore = useUserStore();
const showAccountPopUp = ref(false);

// The single rounded "you are here" box in the bottom bar. Only one item is
// ever active: the More popup takes precedence while open, otherwise the box
// tracks the current page (Home / My Bets). Tapping off closes the popup and
// the box snaps back to whatever page the route resolves to. Search/Deposit
// are actions, not pages, so they never own the box on their own.
const activeItem = computed(() => {
  if (showAccountPopUp.value) {
    return 'more';
  }
  // The search pill can open over any route (it routes to the homepage from
  // pages with no pill), so it takes precedence over the route-based items.
  if (mobileSearchOpen.value) {
    return 'search';
  }
  const path = route.path || '';
  if (path === '/' || String(route.name) === 'HomePage') {
    return 'home';
  }
  if (path.startsWith('/my-bets')) {
    return 'myBets';
  }
  return null;
});

// Tapping the bottom-bar search opens the 4cx-style pill search bar in the
// page header (LeagueHeader on betting pages, the filter row on the homepage)
// via a buffered request the active view consumes — even if the view is still
// loading, its header picks it up once mounted. Only routes with no pill at
// all (e.g. /my-bets/*) fall back to the home search surface.
function openHeaderSearch() {
  // Raise the on-screen keyboard *within this tap gesture* — iOS only opens it
  // from a direct user gesture, and the search pill mounts a few frames later
  // (after a route change for pages with no pill, like my-bets). Focusing the
  // persistent keeper input here opens the keyboard now; because the bottom bar
  // isn't unmounted on navigation, the keeper holds it up across the
  // my-bets -> home route change until the pill's input mounts and focus
  // transfers to it (focusMobileSearch in the pill views).
  searchKeyboardKeeper.value?.focus({ preventScroll: true });
  requestMobileSearch();
  if (!PILL_SEARCH_ROUTES.has(String(route.name))) {
    router.push('/').catch(() => {});
  }
}

function redirect(to) {
  switch (to) {
  case 'myBets':
    router.push('/my-bets/active-wagers');
    break;
  case 'home':
    // Already home: a same-route push is a no-op, which would leave an
    // active search's results on screen — ask the homepage to reset.
    if (route.path === '/' || String(route.name) === 'HomePage') {
      emitter.emit('resetHomeSearch');
    } else {
      router.push('/');
    }
    break;
  default:
    break;
  }
}

// Wallet access — blocked affiliate codes / usernames / demo can't use cashier.
const canUseCashier = computed(() => userCanUseCashier(userStore.user));

function showDepositModal() {
  // Cashier-blocked users have no wallet access — tapping the balance does nothing.
  if (!canUseCashier.value) return;
  if (userStore.user.veriffData?.required === true && userStore.user.veriffData?.status !== 'approved') {
    PostHogUtils.markKycRequired();
    emitter.emit('showVeriffModal');
  } else {
    emitter.emit('showDepositModal');
  }
}

function toggleAccountPopUp(event) {
  if (event === 'icon') {
    showAccountPopUp.value = !showAccountPopUp.value;
  } else {
    showAccountPopUp.value = false;
  }
}

function formatBalance(balance) {
  const absBalance = Math.abs(balance);
  if (absBalance >= 1000) {
    return absBalance.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  }
  return absBalance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function getMobileBalanceText() {
  const balance = userStore.user.displayBalance;
  // Show balance if positive
  if (balance > 0) {
    return '$' + formatBalance(balance);
  }
  // Show "Deposit" if balance is between -10 and 0 — but not for cashier-blocked
  // users, who can't deposit; show their balance instead.
  if (balance >= -10) {
    return canUseCashier.value ? 'Deposit' : '$' + formatBalance(balance);
  }
  // Show negative balance if less than -10
  return '-$' + formatBalance(Math.abs(balance));
}

</script>

<style lang="scss">
@use 'sass:map';
@use '@/scss/main.scss';

.menu {
    position: sticky;
    bottom: 0;
    background-color: main.$color-primary;
    padding: 10px;
    font-size: 11px;
    z-index: 4;
}

.menu-option {
    justify-items: center;
    cursor: pointer;
    padding: 5px 8px;
    margin: -5px -2px;
    border-radius: 11px;
    border: 1px solid transparent;
    background-color: transparent;
    transition: background-color 0.2s ease, border-color 0.2s ease;
    -webkit-tap-highlight-color: transparent;
    touch-action: manipulation;
}

// The rounded "you are here" box. Driven by the single `activeItem` computed,
// so it can only ever sit on one item at a time.
.menu-option.active {
    background-color: rgba(255, 255, 255, 0.08);
    border-color: rgba(255, 255, 255, 0.12);
}

.menu-option-text {
    color: white;
    font-size: 11px;
    padding: 0;
    pointer-events: none;
}

.icon-style {
  width: 20px;
  height: 20px;
  color: white;
  padding-top: 1px;
  pointer-events: none;

  &-blue {
    color: main.$color-primary;
  }
}

.content-align {
    justify-content: space-between;
}

// Invisible 1px input that only exists to hold the on-screen keyboard open
// from the Search tap until the real pill input takes focus. 16px font-size
// avoids iOS zoom-on-focus; it never receives pointer events.
.search-keyboard-keeper {
    position: fixed;
    bottom: 0;
    left: 0;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: 0;
    border: 0;
    background: transparent;
    color: transparent;
    opacity: 0;
    font-size: 16px;
    pointer-events: none;
}

.item-align {
    align-content: center;
    text-align: center;
}

// Mobile balance button - modern pill style
.mobile-balance-btn {
  // Reset
  border: none;
  outline: none;
  cursor: pointer;
  
  // Layout
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem 1.25rem;
  min-width: 90px;
  
  // Shape
  border-radius: 24px;
  
  // Light mode default state (deposit)
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  box-shadow: 0 2px 8px rgba(99, 102, 241, 0.35);
  
  // Transitions
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(99, 102, 241, 0.45);
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: 0 1px 4px rgba(99, 102, 241, 0.3);
  }
  
  &__text {
    color: #ffffff;
    font-size: 0.875rem;
    font-weight: 600;
    letter-spacing: 0.02em;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  }
  
  // Light mode has balance state - green accent
  &--has-balance {
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
    box-shadow: 0 2px 8px rgba(16, 185, 129, 0.35);

    &:hover {
      box-shadow: 0 4px 12px rgba(16, 185, 129, 0.45);
    }

    &:active {
      box-shadow: 0 1px 4px rgba(16, 185, 129, 0.3);
    }
  }
  
  // Night mode styles - softer, muted colors that blend with dark theme
  [theme='night'] & {
    background: linear-gradient(135deg, rgba(150, 156, 203, 0.25) 0%, rgba(134, 144, 219, 0.35) 100%);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
    border: 1px solid rgba(150, 156, 203, 0.3);
    
    &:hover {
      background: linear-gradient(135deg, rgba(150, 156, 203, 0.35) 0%, rgba(134, 144, 219, 0.45) 100%);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.35);
      border-color: rgba(150, 156, 203, 0.5);
    }
    
    &:active {
      background: linear-gradient(135deg, rgba(150, 156, 203, 0.4) 0%, rgba(134, 144, 219, 0.5) 100%);
      box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
    }
  }
  
  // Night mode has balance - teal/cyan accent
  [theme='night'] &--has-balance {
    background: linear-gradient(135deg, rgba(74, 158, 164, 0.35) 0%, rgba(90, 176, 182, 0.45) 100%);
    box-shadow: 0 2px 8px rgba(74, 158, 164, 0.25);
    border: 1px solid rgba(74, 158, 164, 0.4);

    &:hover {
      background: linear-gradient(135deg, rgba(74, 158, 164, 0.45) 0%, rgba(90, 176, 182, 0.55) 100%);
      box-shadow: 0 4px 12px rgba(74, 158, 164, 0.35);
      border-color: rgba(74, 158, 164, 0.6);
    }

    &:active {
      background: linear-gradient(135deg, rgba(74, 158, 164, 0.5) 0%, rgba(90, 176, 182, 0.6) 100%);
      box-shadow: 0 1px 4px rgba(74, 158, 164, 0.2);
    }
  }
}

</style>
